import { useCallback, useEffect, useRef, useState } from 'react';
import { dateVersIso, dtoVersFacture, isoVersDate } from '../api/mappers';
import { facturesApi } from '../api/resources';
import {
  paiementsDansLesRemarques,
  paiementsParFacture,
  sansLesPaiements,
} from '../lib/acomptes-anterieurs';
import { DEVISE, fmt } from '../lib/currency';
import { tr, trf } from '../lib/i18n';
import { today } from '../lib/utils';
import { C } from '../theme/palette';
import { Bar } from './ui/Bar.jsx';
import { Btn } from './ui/Btn.jsx';
import { Card } from './ui/Card.jsx';
import { Inp } from './ui/Inputs.jsx';

/**
 * Les paiements reçus sur les factures, chacun à sa date, chargés une fois.
 *
 * @param {string|number} [version] change quand les factures ont pu changer
 * @returns {{paiements: Map<number, {date: string, montant: number}[]>, recharge: () => void}}
 *   les paiements par facture, date suisse
 */
export function usePaiements(version) {
  const [liste, setListe] = useState([]);
  const [tour, setTour] = useState(0);
  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(() => facturesApi.encaissements())
      .then((l) => vivant && setListe(Array.isArray(l) ? l : []))
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [version, tour]);
  const recharge = useCallback(() => setTour((t) => t + 1), []);
  return { paiements: paiementsParFacture(liste), recharge };
}

/**
 * « 💵 Paiements reçus » d'une facture : chaque versement à sa date.
 *
 * <p>L'acompte versé le 24.12.2025 pour une facture de 2026 se note ici, à
 * sa date : la comptabilité le range dans l'année choisie (Compta →
 * Synthèse). Un paiement écrit dans le message du bas de la facture s'inscrit
 * tout seul à sa date, et sa phrase se retire du bas du document : le
 * paiement figure sous le total. « Annuler » remet tout comme avant.
 *
 * @param {object} props propriétés
 * @param {object} props.facture facture affichée
 * @param {number} props.ttc ce que le client doit en tout, TVA comprise, avoirs déduits
 * @param {{date: string, montant: number}[]} props.paiements paiements déjà inscrits
 * @param {(facture: object) => void} props.onFacture reçoit la facture à jour
 * @param {() => void} props.onPaiements les paiements ont changé
 * @returns {import('react').ReactElement} la carte
 */
export function PaiementsRecus({ facture, ttc, paiements, onFacture, onPaiements }) {
  const [date, setDate] = useState(today());
  const [montant, setMontant] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // Une facture payée avant que les paiements soient datés : son paiement,
  // à sa date de paiement, sert de point de départ.
  const inscrits =
    paiements && paiements.length
      ? paiements
      : +facture.paidAmount > 0
        ? [{ date: facture.paidDate || facture.date, montant: +facture.paidAmount }]
        : [];
  const recu = inscrits.reduce((s, p) => s + p.montant, 0);
  const deja = (d, m) =>
    inscrits.some((p) => dateVersIso(p.date) === d && Math.abs(p.montant - m) < 0.01);
  const proposes = paiementsDansLesRemarques(
    [facture.piedMessage, facture.notes].filter(Boolean).join('\n'),
  ).filter((p) => !deja(p.date, p.montant));

  const enregistre = async (liste) => {
    setErreur('');
    setEnvoi(true);
    try {
      const dto = await facturesApi.remplaceEncaissements(
        facture.id,
        liste.map((p) => ({ date: dateVersIso(p.date), montant: +p.montant })),
      );
      if (dto) onFacture(dtoVersFacture(dto));
      onPaiements();
      return true;
    } catch (e) {
      setErreur(e?.message || tr('Les paiements n’ont pas pu être enregistrés.'));
      return false;
    } finally {
      setEnvoi(false);
    }
  };

  // Un paiement noté dans les remarques remplace d'abord la ligne de reprise :
  // le solde de la facture déjà payée en est diminué d'autant.
  const avecProposes = () => {
    const ajoutes = proposes.map((p) => ({ date: isoVersDate(p.date), montant: p.montant }));
    if (!(paiements && paiements.length) && +facture.paidAmount > 0) {
      const somme = ajoutes.reduce((s, p) => s + p.montant, 0);
      const reste = Math.round((+facture.paidAmount - somme) * 100) / 100;
      return [
        ...ajoutes,
        ...(reste > 0.004 ? [{ date: facture.paidDate || facture.date, montant: reste }] : []),
      ];
    }
    return [...inscrits, ...ajoutes];
  };

  // Le paiement écrit en bas de la facture (« acompte de CHF 7'807.– versé le
  // 24.12.2025 ») s'inscrit tout seul, une fois par facture ouverte, et sa
  // phrase quitte le bas du document.
  const [fait, setFait] = useState(null);
  const tente = useRef(null);
  const message = facture.piedMessage || '';
  const ecrits = paiementsDansLesRemarques(message);
  useEffect(() => {
    if (!ecrits.length || tente.current === facture.id) return;
    tente.current = facture.id;
    const avant = { paiements: inscrits, message };
    (async () => {
      if (proposes.length && !(await enregistre(avecProposes()))) return;
      try {
        await facturesApi.messagePied(facture.id, sansLesPaiements(message));
        onFacture(facture);
        setFait({ ...avant, ecrits });
      } catch (e) {
        setErreur(e?.message || tr('Le message de la facture n’a pas pu être changé.'));
      }
    })();
    // Une fois par facture : les paiements et le message changent ensuite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facture.id, ecrits.length]);

  const annule = async () => {
    if (!fait) return;
    if (await enregistre(fait.paiements)) {
      try {
        await facturesApi.messagePied(facture.id, fait.message);
        onFacture(facture);
      } catch (e) {
        setErreur(e?.message || tr('Le message de la facture n’a pas pu être changé.'));
      }
    }
    setFait(null);
  };

  return (
    <Card style={{ marginBottom: 8 }}>
      <Bar
        icon="💵"
        title={tr('Paiements reçus')}
        color={C.amber}
        sub={trf(
          'Reçu {0} · reste {1}',
          DEVISE + ' ' + fmt(recu),
          DEVISE + ' ' + fmt(Math.max(0, ttc - recu)),
        )}
      />
      <div style={{ padding: 13 }}>
        {fait && (
          <div
            role="status"
            style={{
              background: C.greenL,
              color: C.green,
              borderRadius: 9,
              padding: '9px 11px',
              marginBottom: 10,
              fontSize: 11,
              lineHeight: 1.5,
            }}
          >
            {fait.ecrits.map((p) => (
              <div key={p.date + p.montant} style={{ fontWeight: 800 }}>
                {trf(
                  '✓ {0} reçu le {1} : inscrit à sa date, compté en {2}.',
                  DEVISE + ' ' + fmt(p.montant),
                  isoVersDate(p.date),
                  p.date.slice(0, 4),
                )}
              </div>
            ))}
            <div style={{ marginTop: 3 }}>
              {tr(
                'La phrase est retirée du bas de la facture : le paiement figure maintenant sous le total.',
              )}
            </div>
            <button
              type="button"
              onClick={annule}
              disabled={envoi}
              style={{
                marginTop: 6,
                border: 'none',
                background: 'none',
                color: C.green,
                fontWeight: 800,
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                fontSize: 11,
              }}
            >
              {tr('Annuler')}
            </button>
          </div>
        )}
        {inscrits.map((p, i) => (
          <div
            key={p.date + '-' + i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 0',
              borderBottom: '1px solid ' + C.border,
              fontSize: 12,
            }}
          >
            <span style={{ flex: 1 }}>{p.date}</span>
            <b>
              {DEVISE} {fmt(p.montant)}
            </b>
            <button
              type="button"
              aria-label={tr('Supprimer ce paiement')}
              disabled={envoi}
              onClick={() => enregistre(inscrits.filter((_, j) => j !== i))}
              style={{
                background: C.redL,
                border: 'none',
                borderRadius: 7,
                color: C.red,
                padding: '4px 8px',
                cursor: 'pointer',
              }}
            >
              🗑
            </button>
          </div>
        ))}
        {proposes.length > 0 && (
          <div
            style={{
              background: C.purpleL,
              color: C.purple,
              borderRadius: 9,
              padding: '9px 11px',
              margin: '10px 0',
              fontSize: 11,
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 4 }}>
              {tr('💡 Trouvé dans les remarques de la facture :')}
            </div>
            {proposes.map((p) => (
              <div key={p.date + p.montant}>
                {trf('{0} reçu le {1}', DEVISE + ' ' + fmt(p.montant), isoVersDate(p.date))}
              </div>
            ))}
            <div style={{ marginTop: 8 }}>
              <Btn
                label={tr('✓ Inscrire ces paiements à leur date')}
                color={C.purple}
                sm
                full
                disabled={envoi}
                onClick={() => enregistre(avecProposes())}
              />
            </div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
          <Inp
            label={tr('Date du paiement')}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Inp
            label={trf('Montant reçu ({0})', DEVISE)}
            type="number"
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
          />
        </div>
        <Btn
          label={tr('＋ Ajouter ce paiement')}
          color={C.amber}
          full
          disabled={envoi || !(parseFloat(String(montant).replace(',', '.')) > 0) || !date}
          onClick={async () => {
            const m = parseFloat(String(montant).replace(',', '.'));
            await enregistre([...inscrits, { date, montant: m }]);
            setMontant('');
          }}
        />
        {erreur && (
          <div role="alert" style={{ color: C.red, fontSize: 10.5, marginTop: 8 }}>
            {erreur}
          </div>
        )}
      </div>
    </Card>
  );
}
